/**
 * Atrybucja kampanii: przechwytuje parametry UTM (oraz identyfikatory kliknięć
 * reklam: gclid, fbclid, msclkid, ttclid, li_fat_id) z adresu strony wejścia
 * i utrzymuje je przez całą wizytę, żeby każde zdarzenie analityczne wiedziało,
 * skąd przyszedł użytkownik.
 *
 * Zasada: first-touch w obrębie sesji. Pierwsze wejście z parametrami ustala
 * atrybucję; kolejne podstrony bez parametrów jej nie kasują. Nowe wejście
 * z nowymi parametrami UTM nadpisuje poprzednie.
 */

const STORAGE_KEY = "ss:attribution";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const CLICK_ID_KEYS = ["gclid", "fbclid", "msclkid", "ttclid", "li_fat_id"] as const;

export type Attribution = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  /** Identyfikator kliknięcia reklamy, w formacie "gclid:CjwK..." */
  click_id: string | null;
  /** Pierwsza strona wizyty (ścieżka + parametry). */
  landing_page: string | null;
  /** Strona odsyłająca dla pierwszego wejścia w tej sesji. */
  referrer: string | null;
};

const EMPTY: Attribution = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
  click_id: null,
  landing_page: null,
  referrer: null,
};

let cached: Attribution | null = null;

function clean(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim().slice(0, 200);
  return trimmed.length > 0 ? trimmed : null;
}

function read(): Attribution {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    return { ...EMPTY, ...(JSON.parse(raw) as Partial<Attribution>) };
  } catch {
    return { ...EMPTY };
  }
}

function write(value: Attribution): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // tryb prywatny bywa bez sessionStorage; atrybucja zostaje wtedy w pamięci
  }
}

/** Domyślne źródło, gdy w adresie nie ma UTM-ów: ruch bezpośredni albo odesłanie. */
function inferredSource(referrer: string | null): { source: string; medium: string } {
  if (!referrer) return { source: "direct", medium: "none" };
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "");
    if (host === window.location.hostname.replace(/^www\./, "")) {
      return { source: "direct", medium: "none" };
    }
    const search = /google|bing|duckduckgo|yahoo|ecosia|yandex/i.test(host);
    return { source: host, medium: search ? "organic" : "referral" };
  } catch {
    return { source: "direct", medium: "none" };
  }
}

/**
 * Odczytuje parametry z bieżącego adresu i zapisuje atrybucję dla wizyty.
 * Wywoływane przy starcie aplikacji i po każdej zmianie adresu.
 */
export function captureAttribution(): Attribution {
  if (typeof window === "undefined") return { ...EMPTY };

  const stored = read();
  const params = new URLSearchParams(window.location.search);

  const utm: Partial<Attribution> = {};
  let hasUtm = false;
  for (const key of UTM_KEYS) {
    const value = clean(params.get(key));
    if (value) {
      hasUtm = true;
      utm[key] = value;
    }
  }

  let clickId: string | null = null;
  for (const key of CLICK_ID_KEYS) {
    const value = clean(params.get(key));
    if (value) {
      clickId = `${key}:${value}`;
      break;
    }
  }

  const referrer = clean(document.referrer);
  const landing = `${window.location.pathname}${window.location.search}`.slice(0, 300);

  // nowe wejście kampanijne nadpisuje poprzednią atrybucję
  if (hasUtm || clickId) {
    const fallback = inferredSource(referrer);
    cached = {
      ...EMPTY,
      ...utm,
      utm_source: utm.utm_source ?? (clickId ? clickId.split(":")[0]! : fallback.source),
      utm_medium: utm.utm_medium ?? (clickId ? "cpc" : fallback.medium),
      click_id: clickId,
      landing_page: landing,
      referrer,
    };
    write(cached);
    return cached;
  }

  // pierwsze wejście w sesji bez UTM: zapisujemy źródło wywnioskowane z referrera
  if (!stored.landing_page) {
    const fallback = inferredSource(referrer);
    cached = {
      ...EMPTY,
      utm_source: fallback.source,
      utm_medium: fallback.medium,
      landing_page: landing,
      referrer,
    };
    write(cached);
    return cached;
  }

  cached = stored;
  return cached;
}

/** Aktualna atrybucja wizyty (bez odczytu adresu). */
export function getAttribution(): Attribution {
  if (typeof window === "undefined") return { ...EMPTY };
  if (!cached) return captureAttribution();
  return cached;
}

/** Wersja płaska do payloadu GA4 / GTM / Plausible (bez wartości pustych). */
export function attributionProps(): Record<string, string> {
  const attribution = getAttribution();
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(attribution)) {
    if (value) out[key] = value;
  }
  return out;
}
